import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { uploadPhotos } from '../../firebase/storageService';
import { useAuth } from '../../contexts/AuthContext';
import { isCarrierOrder } from '../../utils/carrierOrder';
import { COLORS } from '../../styles/tokens';

// Fra tildelt til levert (samme perioden som reglene tillater å legge til billag)
const ACTIVE_STATUSES = ['matched', 'in_progress', 'delivered_pending'];
export const MAX_RECEIPTS = 30;
const MAX_NOTE = 200;

// Billag (veiesedler og kvitteringer) som sjåføren laster opp fritt mens oppdraget er aktivt.
// Lagres som `receipts: [{ url, note }]` på ordren, ett nytt element om gangen (det reglene krever).
export default function ReceiptsPanel({ order }) {
  const { currentUser } = useAuth();
  const [file, setFile] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!order || !currentUser?.uid || order.driverUid !== currentUser.uid) return null;
  if (isCarrierOrder(order) || !ACTIVE_STATUSES.includes(order.status)) return null;

  const receipts = order.receipts ?? [];
  const full = receipts.length >= MAX_RECEIPTS;

  function pick(e) {
    setFile(e.target.files?.[0] ?? null);
    setError('');
    e.target.value = '';
  }

  function cancel() {
    setFile(null);
    setNote('');
    setError('');
  }

  async function save() {
    if (!file || busy) return;
    setBusy(true);
    setError('');
    try {
      const [url] = await uploadPhotos([file], `deliveries/${order.id}/receipts`);
      const text = note.trim();
      await updateDoc(doc(db, 'orders', order.id), {
        receipts: [...receipts, text ? { url, note: text } : { url }],
      });
      setFile(null);
      setNote('');
    } catch (err) {
      console.error('ReceiptsPanel:', err);
      setError('Kunne ikke laste opp billaget. Sjekk nettet og prøv igjen.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={styles.box}>
      <div style={styles.title}>Billag</div>

      {receipts.length > 0 && (
        <ul style={styles.list}>
          {receipts.map((r, i) => (
            <li key={`${i}-${r.url}`} style={styles.item}>
              <a href={r.url} target="_blank" rel="noopener noreferrer" style={styles.link}>Billag {i + 1}</a>
              {r.note && <span style={styles.note}>{r.note}</span>}
            </li>
          ))}
        </ul>
      )}

      {full ? (
        <p style={styles.info}>Du har lastet opp maks 30 billag.</p>
      ) : !file ? (
        <>
          <label style={styles.pick}>
            Last opp billag
            <input type="file" accept="image/*" capture="environment" onChange={pick} style={{ display: 'none' }} />
          </label>
          <p style={styles.hint}>Veiesedler og kvitteringer. Bestiller ser dem på oppdraget.</p>
        </>
      ) : (
        <div style={styles.form}>
          <div style={styles.info}>✓ {file.name}</div>
          <label htmlFor="receipt-note" style={styles.label}>Tekst (valgfri)</label>
          <input id="receipt-note" type="text" value={note} maxLength={MAX_NOTE} style={styles.input}
            placeholder="F.eks. veiesedel fra mottaket"
            onChange={e => setNote(e.target.value)} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" style={styles.primary} disabled={busy} onClick={save}>
              {busy ? 'Laster opp…' : 'Lagre billag'}
            </button>
            <button type="button" style={styles.secondary} disabled={busy} onClick={cancel}>Avbryt</button>
          </div>
        </div>
      )}

      {error && <p role="alert" style={styles.error}>{error}</p>}
    </div>
  );
}

const styles = {
  box:       { background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: 14, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 10 },
  title:     { fontSize: 12, fontWeight: 700, color: COLORS.textLight, textTransform: 'uppercase', letterSpacing: 0.3 },
  list:      { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 },
  item:      { display: 'flex', flexDirection: 'column', gap: 2, fontSize: 14 },
  link:      { color: COLORS.textDark, fontWeight: 600 },
  note:      { fontSize: 13, color: COLORS.textMid },
  pick:      { display: 'block', textAlign: 'center', padding: '12px 8px', borderRadius: 26, border: `2px solid ${COLORS.primary}`, background: COLORS.white, color: COLORS.textDark, fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  hint:      { fontSize: 12, color: COLORS.textLight, margin: 0, lineHeight: 1.4 },
  info:      { fontSize: 14, color: COLORS.textDark, margin: 0 },
  form:      { display: 'flex', flexDirection: 'column', gap: 8 },
  label:     { fontSize: 12, fontWeight: 700, color: COLORS.textLight },
  input:     { width: '100%', boxSizing: 'border-box', borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: 10, fontSize: 14, fontFamily: 'inherit' },
  primary:   { height: 44, borderRadius: 22, border: 'none', background: COLORS.primary, color: COLORS.onPrimary, fontWeight: 700, fontSize: 14, cursor: 'pointer', padding: '0 18px' },
  secondary: { height: 40, borderRadius: 20, border: `1px solid ${COLORS.border}`, background: COLORS.white, color: COLORS.textDark, fontWeight: 600, fontSize: 14, cursor: 'pointer', padding: '0 16px' },
  error:     { background: COLORS.dangerBg, color: COLORS.dangerText, padding: '10px 14px', borderRadius: 6, fontSize: 14, margin: 0 },
};
